/**
 * Teacher dashboard — fetches /api/dashboard for top-level counts.
 * Premium editorial layout with serif headings and gold accents.
 */
import { useEffect, useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { api } from '../../lib/api';
import { useAuth } from '../../stores/auth';

interface DashboardCounts {
  student_count: number;
  problem_count: number;
  set_count: number;
  assignment_count: number;
  submission_done: number;
}

interface DashboardStudentRow {
  id: string;
  name: string;
  total_submissions: number;
  avg_score: number | null;
}

interface DashboardResponse {
  counts: DashboardCounts;
  students: DashboardStudentRow[];
}

export function TeacherHome() {
  const navigate = useNavigate();
  const { user, logout } = useAuth();
  const [data, setData] = useState<DashboardResponse | null>(null);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    let active = true;
    (async () => {
      try {
        const res = await api.get<DashboardResponse>('/api/dashboard');
        if (active) setData(res);
      } catch (e) {
        if (active) setError((e as Error).message);
      }
    })();
    return () => {
      active = false;
    };
  }, []);

  async function handleLogout() {
    await logout();
    navigate('/login', { replace: true });
  }

  return (
    <main className="page">
      <header className="header">
        <h1 data-testid="teacher-home-title">
          {user?.name} 선생님
          <span className="badge">Teacher</span>
        </h1>
        <button onClick={handleLogout} className="btn btn-ghost" data-testid="logout-button">
          Logout
        </button>
      </header>

      <nav className="nav" aria-label="관리 메뉴">
        <Link to="/teacher/students" data-testid="nav-students">
          학생 관리
        </Link>
        <Link to="/teacher/problems" data-testid="nav-problems">
          문제 관리
        </Link>
      </nav>

      {error && <p className="error-text" role="alert">{error}</p>}

      {!data && !error && (
        <div className="loading" role="status" aria-label="불러오는 중">
          <span>불러오는 중…</span>
        </div>
      )}

      {data && (
        <section data-testid="dashboard-summary" aria-label="대시보드 요약">
          <div className="stat-row">
            <div className="stat-card" data-testid="card-students">
              <div className="stat-label">Students</div>
              <div className="stat-value">{data.counts.student_count}</div>
            </div>
            <div className="stat-card" data-testid="card-assignments">
              <div className="stat-label">Assignments</div>
              <div className="stat-value">{data.counts.assignment_count}</div>
            </div>
            <div className="stat-card" data-testid="card-submissions">
              <div className="stat-label">Completed</div>
              <div className="stat-value">{data.counts.submission_done}</div>
            </div>
          </div>

          <p className="section-title">Student Performance</p>

          {data.students.length === 0 ? (
            <div className="empty-state">
              <p>아직 등록된 학생이 없습니다</p>
              <p className="list-item-meta mt-2">학생 관리 메뉴에서 학생을 추가하세요</p>
            </div>
          ) : (
          <div className="table-wrap">
            <table aria-label="학생별 통계">
              <thead>
                <tr>
                  <th>학생</th>
                  <th>제출</th>
                  <th>평균</th>
                </tr>
              </thead>
              <tbody>
                {data.students.map((s) => (
                  <tr key={s.id} data-testid={`row-${s.id}`}>
                    <td className="font-medium">{s.name}</td>
                    <td>{s.total_submissions}</td>
                    <td>
                      {s.avg_score != null ? (
                        <span className="score">
                          <span className="score-number">
                            {Math.round(s.avg_score)}
                          </span>
                          <span className="score-unit">점</span>
                        </span>
                      ) : (
                        '—'
                      )}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
          )}
        </section>
      )}
    </main>
  );
}
